/**
 * `createEnrichedView` — build a virtual view that reshapes ONE source CDF
 * view (a fixed `where` + de-prefix/rename projection) AND appends, per row,
 * optional FlatHierarchy-derived columns:
 *
 *   - ANCESTRY (`enrich`) — the row's hierarchy ancestors as
 *     `{ space, externalId, name }` struct columns (one per `AncestorRelation`),
 *     resolved by `enrichWithAncestors`;
 *   - distinct-descendant COUNTS (`counts`) — the number of distinct
 *     descendant refs each row fans out to per relation, as `integerCol`
 *     columns, resolved by `countDescendants`.
 *
 * The driving ref (the parent / producing-string the FlatHierarchy read is
 * keyed on) is sourced via `RefSource`: `'column'` reads it off a kept
 * canonical struct column (the string-grain case, e.g. `wellString`); `'self'`
 * builds it from the row's OWN identity (`space` + `externalId` — the
 * entity-grain case, where the row itself is the field / reservoir / string the
 * hierarchy is keyed on). A view may request ancestry, counts, both, or
 * neither (a pure reshape — but then prefer `createReshapedView`).
 *
 * Unlike `createReshapedView` (a pure `rewrite` facade), an enriched view
 * cannot expand to a single s-expression: the ancestry / counts come from
 * SEPARATE views (FlatHierarchy [→ Entity]), resolved against the fetched rows.
 * So this factory compiles to a custom-FETCH LEAF provider (`plan` + `fetch`):
 *
 *   - `plan` pushes ONLY the fixed `where` (predicate) + the source-column
 *     projection into the native CDF query; EVERY agent op
 *     (filter/sort/limit/columns) rides as a RESIDUAL replayed client-side
 *     over the canonical surface (`isPushable: () => false`). We never push
 *     an agent predicate on a canonical / struct-subpath name in Phase 1.
 *   - `fetch` runs the native query, de-prefixes/renames source → canonical
 *     (the `resolveColumnSet` SSOT), resolves ancestry and/or counts off the
 *     driving ref, and assembles the canonical + struct + count columns into
 *     one `MaterializedDataset`.
 *
 * The fixed `where` is pushed via `native.filter`: it is authored over the
 * kept passthrough columns (identity / category), where source === canonical,
 * so it translates directly against the source view by `buildCdfViewQuery`.
 *
 * Column TYPES and agent DOCS for the reshaped columns are derived from the
 * source view via `resolveColumnSet`; the ancestor columns add one
 * `ancestorStruct` to the engine schema and one struct `ColumnDoc` to the
 * docs per relation, and each count adds one `integerCol` + one number doc.
 */

import type { CogniteClient } from '@cognite/sdk';

import type { DataModelView } from '#core/lib/cognite/view.ts';
import { instancesToColumns, type MintSeriesSource, nodeIdentity } from '#core/lib/cognite/view.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import {
  type ColumnDoc,
  integerCol,
  numberCol,
  projectSchema,
  type Schema,
  schemaFromEntries,
  type ViewDescription,
} from '#core/lib/expr/schema/types.ts';
import { type Cell, MaterializedDataset } from '#core/lib/expr/value.ts';
import type { InstanceIdType } from '#core/schema/defs.ts';

import {
  type AncestorScopedAssembleArgs,
  describeAncestorColumns,
  makeAncestorScopedProvider,
} from './ancestorScope.ts';
import type { VirtualView } from './createReshapedView.ts';
import {
  type AncestorRelation,
  ancestorStruct,
  asRef,
  countDescendants,
  enrichWithAncestors,
} from './enrichAncestors.ts';
import { assembleCanonicalColumns, resolveColumnSet, sourceColumnsOf } from './resolveColumns.ts';
import type { EnrichGroup, Shape } from './shapes/index.ts';

/**
 * Where the per-row ref that drives FlatHierarchy resolution (ancestry +
 * counts) comes from:
 *
 *   - `'column'` (default) — read it off a kept canonical struct column (a
 *     producing-string relation, e.g. `wellString`). The string-grain case.
 *   - `'self'` — build it from each row's OWN identity (`{ space, externalId }`
 *     from the canonical `space` / `externalId` columns). The entity-grain case
 *     (per-type *Entity views), where the row itself is the
 *     string / field / reservoir / … the hierarchy is keyed on.
 */
export type RefSource = 'column' | 'self';

/** The ancestry-enrichment block: which relations to resolve, and the ref that drives them. */
export type EnrichSpec = {
  /**
   * The producing-string ref. With `from: 'column'` (default) this is the
   * canonical column carrying the `{ space, externalId }` ref; with
   * `from: 'self'` it is omitted (the ref is the row's own identity).
   */
  readonly stringRefColumn?: string;
  /** Where the resolving ref comes from. Defaults to `'column'`. */
  readonly from?: RefSource;
  /**
   * The FlatHierarchy column the row's own identity matches when `from: 'self'`
   * — the producing-string column by default, or a coarser ancestor relation
   * (e.g. `well`) for an entity view at that grain.
   */
  readonly selfColumn?: string;
  /** The hierarchy relations to append as `{ space, externalId, name }` struct columns. */
  readonly relations: readonly AncestorRelation[];
};

/**
 * One distinct-descendant count column: an `integerCol` whose value is the
 * number of distinct `descendantRelation` refs reachable from this row's parent
 * ref through `FlatHierarchy_Agent`, filtered on the `parentColumn` relation.
 */
export type CountSpec = {
  /** Canonical, agent-facing count column name (e.g. `wellCount`). */
  readonly column: string;
  /** The FlatHierarchy direct-relation column whose distinct refs are counted (e.g. `well`). */
  readonly descendantRelation: string;
  /** Agent-facing description of the count column. */
  readonly description: string;
};

/**
 * The counts block: the FlatHierarchy parent relation each row is matched on
 * plus the distinct-descendant count columns to append. The parent ref is the
 * resolving ref (see {@link EnrichSpec}/{@link RefSource}); for the entity-grain
 * count views it is the row's own identity (`from: 'self'`).
 */
export type CountsSpec = {
  /** The FlatHierarchy direct-relation column the parent ref is matched on (e.g. `field`). */
  readonly parentColumn: string;
  /** Where the parent ref comes from. Defaults to `'column'`. */
  readonly from?: RefSource;
  /**
   * The canonical column carrying the parent `{ space, externalId }` ref when
   * `from: 'column'`; omitted when `from: 'self'` (the row's own identity).
   */
  readonly parentRefColumn?: string;
  /** The distinct-descendant count columns to append. */
  readonly counts: readonly CountSpec[];
};

/**
 * Declarative spec for an enriched view: ONE reshaped source CDF view, plus
 * optional per-row FlatHierarchy enrichment — ancestry struct columns
 * (`enrich`) and/or distinct-descendant count columns (`counts`). Both ride the
 * same custom-FETCH leaf and share the FlatHierarchy read seam; a view may have
 * neither (a pure reshape — but then prefer `createReshapedView`), one, or both.
 */
export type EnrichedViewSpec = {
  /** Agent-facing registry name (distinct from the source externalId). */
  readonly name: string;
  /** View-level description shown to the agent by the DescribeViews tool. */
  readonly description: string;
  /** The source CDF view this reshapes + enriches. */
  readonly source: DataModelView;
  /** A fixed predicate body over `%` pushed into the native query (e.g. `eq('entityType','well')`). */
  readonly where?: AstInput;
  /** Passthrough columns kept under their source names (identity, relations, category). */
  readonly keep: Shape;
  /** Prefixed/renamed column groups (the de-prefixed measures). */
  readonly groups?: readonly EnrichGroup[];
  /** Ancestor struct columns resolved off the producing-string / self ref. Omit for a counts-only view. */
  readonly enrich?: EnrichSpec;
  /** Distinct-descendant count columns resolved off the parent / self ref. Omit for an ancestry-only view. */
  readonly counts?: CountsSpec;
  /** The views + client the FlatHierarchy / Entity reads run through. */
  readonly client: CogniteClient;
  /**
   * The timeseries minter (`useSessionTimeseries().buildSource`) every per-row
   * timeseries cell is built through. Optional only because `buildSource` is
   * optional on the provider type; the fetch eager-throws if it is absent, so a
   * source carrying a timeseries column always has a real minter.
   */
  readonly mintSeries?: MintSeriesSource;
};

/**
 * Resolve the per-row `{ space, externalId }` ref that drives FlatHierarchy
 * resolution, off the de-prefixed canonical columns. With `from: 'self'` the
 * ref is the row's own identity (`space` + `externalId` columns); with
 * `from: 'column'` it is decoded off the named canonical struct column. Shared
 * verbatim by the ancestry and counts paths so the two never derive the ref
 * differently.
 */
const resolveDrivingRefs = (
  canonical: Record<string, readonly Cell[]>,
  from: RefSource,
  refColumn: string | undefined,
): readonly (InstanceIdType | null)[] => {
  if (from === 'self') {
    const spaces: readonly Cell[] = canonical.space ?? [];
    const externalIds: readonly Cell[] = canonical.externalId ?? [];
    return externalIds.map((externalId: Cell, i: number): InstanceIdType | null => {
      const space = spaces[i];
      return typeof space === 'string' && typeof externalId === 'string' ? { space, externalId } : null;
    });
  }
  if (refColumn === undefined) {
    throw new Error("createEnrichedView: a 'column' ref source requires a ref column name");
  }
  return (canonical[refColumn] ?? []).map(asRef);
};

export const createEnrichedView = (spec: EnrichedViewSpec): VirtualView => {
  const { source, where, keep, groups, enrich, counts, client, mintSeries } = spec;
  if (!mintSeries) {
    throw new Error('mintSeries required to mint per-row view timeseries cells');
  }
  const relations: readonly AncestorRelation[] = enrich?.relations ?? [];
  const countSpecs: readonly CountSpec[] = counts?.counts ?? [];
  // SSOT for de-prefix/rename/typing/docs of the reshaped columns — shared
  // verbatim with the reshape/flatten factories.
  const { cols, declared: reshapedSchema, columns: reshapedDocs, mintSchema } = resolveColumnSet(source, keep, groups);

  // The engine schema = reshaped canonical columns + one ancestorStruct per
  // requested relation + one integerCol per distinct-descendant count.
  const declared: Schema = schemaFromEntries([
    ...reshapedSchema.entries(),
    ...relations.map((rel: AncestorRelation) => [rel, ancestorStruct] as const),
    ...countSpecs.map((c: CountSpec) => [c.column, integerCol] as const),
  ]);

  // Agent docs = reshaped column docs + one struct ColumnDoc per relation +
  // one number ColumnDoc per count.
  const ancestorDocs: ColumnDoc[] = describeAncestorColumns(relations);
  const countDocs: ColumnDoc[] = countSpecs.map(
    (c: CountSpec): ColumnDoc => ({ name: c.column, type: numberCol, description: c.description }),
  );
  const viewDoc: ViewDescription = {
    description: spec.description,
    columns: [...reshapedDocs, ...ancestorDocs, ...countDocs],
  };

  // The source-column projection the native query reads (the `keep`/group
  // source names, before the de-prefix rename; a struct column reads every
  // member source).
  const sourceColumns: readonly string[] = sourceColumnsOf(cols);

  // The scaffold + fetch HEAD (ancestor-scope resolution + scoped source read)
  // are shared with the flatten/align factories; the canonical/ancestor/count
  // assembly below is this builder's projection step.
  return makeAncestorScopedProvider({
    name: spec.name,
    declared,
    viewDoc,
    source,
    cols,
    sourceColumns,
    relations,
    enrich,
    client,
    mintSeries,
    where,
    assemble: async ({ native, rows, ctx }: AncestorScopedAssembleArgs): Promise<MaterializedDataset> => {
      // Mint against the unit-stamped `mintSchema` (source-named) so ts-ref
      // value cells are BOXED with their slug unit — `viewToSchema(source)`
      // types the inner value `unknown` and mints unit-less cells.
      const sourceSchema = projectSchema(mintSchema, native.columns);
      const projected = instancesToColumns(rows, sourceSchema, source, mintSeries);

      // De-prefix / rename source → canonical, assembling struct columns from
      // their member sources.
      const canonical: Record<string, readonly Cell[]> = assembleCanonicalColumns(cols, projected);

      // Resolve the producing-string ancestry into per-relation struct
      // columns aligned 1:1 with the fetched rows (skipped when no `enrich`).
      const ancestorColumns: Record<string, readonly Cell[]> = enrich
        ? await (async () => {
            const refs = resolveDrivingRefs(canonical, enrich.from ?? 'column', enrich.stringRefColumn);
            const ancestors = await enrichWithAncestors(client, ctx, refs, relations, mintSeries, enrich.selfColumn);
            return Object.fromEntries(relations.map((rel: AncestorRelation) => [rel, ancestors[rel]] as const));
          })()
        : {};

      // Resolve the distinct-descendant counts into per-column integer
      // columns aligned 1:1 with the fetched rows (skipped when no `counts`).
      const countColumns: Record<string, readonly Cell[]> = counts
        ? await (async () => {
            const refs = resolveDrivingRefs(canonical, counts.from ?? 'column', counts.parentRefColumn);
            const descendantRelations: readonly string[] = countSpecs.map((c: CountSpec) => c.descendantRelation);
            const byRelation = await countDescendants(
              client,
              ctx,
              refs,
              counts.parentColumn,
              descendantRelations,
              mintSeries,
            );
            return Object.fromEntries(
              countSpecs.map((c: CountSpec) => [c.column, byRelation[c.descendantRelation] ?? []] as const),
            );
          })()
        : {};

      const columns: Record<string, readonly Cell[]> = { ...canonical, ...ancestorColumns, ...countColumns };
      // Populate the typed identity axis from the fetched node rows so it
      // aligns 1:1 with the projected space/externalId columns — one row per
      // instance, so a relation-membership set-ref over this view recovers
      // the full {space, externalId} identity instead of failing loud.
      return new MaterializedDataset(columns, declared, rows.map(nodeIdentity));
    },
  });
};
